import React, { useState, useEffect } from "react";
import { useAuth } from "../contexts/AuthContext";
import { supabase } from "../lib/supabase";
import toast from "react-hot-toast";
import {
  Menu,
  Bell,
  TrendingUp,
  History,
  BarChart3,
  Calendar,
  Activity,
  ArrowUpRight,
  ArrowDownRight,
  Search,
  ThumbsUp, 
  ThumbsDown, 
  CheckCircle2 
} from "lucide-react";
import StockCard from "../components/StockCard";
import { CallStatus, StockCall } from "../types";
import Sidebar from "../components/Sidebar";
import logo from "../assets/logo64.png";
import { useNavigate } from "react-router-dom";
import CarouselIndices from "../components/Caraousal";



const Home: React.FC = () => {
  const { profile, signOut } = useAuth();
  const [activeTab, setActiveTab] = useState<
    "today" | "positions" | "historical" | "sentiment"
  >("today");
  const [todaysCalls, setTodaysCalls] = useState<StockCall[][]>([]);
  const [positionalCalls, setPositionalCalls] = useState<StockCall[][]>([]);
  const [dateFilteredCalls, setDateFilteredCalls] = useState<StockCall[][]>([]);
  const [callStatuses, setCallStatuses] = useState<Record<string, CallStatus>>(
    {},
  );
  const [loading, setLoading] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const navigate = useNavigate();
  // Historical & Sentiment States
  const [startDate, setStartDate] = useState<string>("");
  const [endDate, setEndDate] = useState<string>("");
  const [voted, setVoted] = useState<"profit" | "loss" | null>(null); // Local state for user vote
  const [historicalLoading, setHistoricalLoading] = useState(false);
  const [selectedTimeframes, setSelectedTimeframes] = useState<string[]>([
    "5min",
  ]);

  // --- LIVE MARKET TICKER STATE (Simulated) ---
  const indices = {
  nifty: { val: 22450.12, pct: 0.85 },
  sensex: { val: 74200.45, pct: -0.42 },
  bnf: { val: 45200.78, pct: 1.12 },
};


  const timeframeOptions = [
    "3min",
    "5min",
    "15min",
    "45min",
    "1hour",
    "2hours",
    "4hours",
    "1day",
  ];

  // // --- PRICE SIMULATION EFFECT ---
  // useEffect(() => {
  //   const timer = setInterval(() => {
  //     setIndices((prev) => ({
  //       nifty: {
  //         ...prev.nifty,
  //         val: prev.nifty.val + (Math.random() - 0.5) * 3,
  //       },
  //       sensex: {
  //         ...prev.sensex,
  //         val: prev.sensex.val + (Math.random() - 0.5) * 8,
  //       },
  //       bnf: { ...prev.bnf, val: prev.bnf.val + (Math.random() - 0.5) * 6 },
  //     }));
  //   }, 3000);
  //   return () => clearInterval(timer);
  // }, []);

  // --- BUSINESS LOGIC & SUPABASE ---
  useEffect(() => {
    if (profile) {
      console.log("User profile:", profile);
      if (profile.admin === "Y") {
        navigate("/adminhome");
      } else {
        navigate("/home");
      }
      loadCalls();
      const unsubscribe = subscribeToRealtimeUpdates();
      return () => {
        unsubscribe();
      };
    }
  }, [profile]);

  const loadCalls = async () => {
    if (!profile?.plan_id) return;
    try {
      setLoading(true);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const tomorrow = new Date(today);
      tomorrow.setDate(tomorrow.getDate() + 1);

      // 1. Get Plan Details
      const { data: planData } = await supabase
        .from("Vitta_Plandetails")
        .select("in_type_id")
        .eq("plan_id", profile.plan_id);
      console.log("data",planData)
      const typeIds = planData?.map((p) => p.in_type_id) || [];
      console.log("typeid",typeIds)
      // 2. Fetch Today's Calls
      const { data: todayData } = await supabase
        .from("Vitta_Calls")
        .select("*")
        .gte("created_on", today.toISOString())
        .lt("created_on", tomorrow.toISOString())
        .eq("trade_type", "intraday")
        .in("instrument_type", typeIds)
        .order("created_on", { ascending: false });
      console.log(todayData)
      // 3. Fetch Positional Calls (Last 30 days)
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
      const { data: positionalData } = await supabase
        .from("Vitta_Calls")
        .select("*")
        .gte("created_on", thirtyDaysAgo.toISOString())
        .lt("created_on", today.toISOString())
        .eq("trade_type", "positional")
        .in("instrument_type", typeIds)
        .limit(50);

      // Map and group calls
      const mappedTodayCalls = mapToStockCalls(todayData || []);
      const groupedTodayCalls =
        groupCallsBySourceRecommendation(mappedTodayCalls);
      setTodaysCalls(groupedTodayCalls);

      const mappedPositionalCalls = mapToStockCalls(positionalData || []);
      const groupedPositionalCalls = groupCallsBySourceRecommendation(
        mappedPositionalCalls,
      );
      setPositionalCalls(groupedPositionalCalls);
    } catch (error) {
      toast.error("Network Sync Failed");
    } finally {
      setLoading(false);
    }
  };

  const loadHistoricalByDateRange = async () => {
    if (!startDate || !endDate) return toast.error("Select date range");
    try {
      setHistoricalLoading(true);
      const { data } = await supabase
        .from("Vitta_Calls")
        .select("*")
        .gte("created_on", new Date(startDate).toISOString())
        .lte("created_on", new Date(endDate).toISOString())
        .order("created_on", { ascending: false });

      const mappedCalls = mapToStockCalls(data || []);
      const groupedCalls = groupCallsBySourceRecommendation(mappedCalls);
      setDateFilteredCalls(groupedCalls);
      toast.success(`Retrieved ${data?.length} records`);
    } catch (e) {
      toast.error("Historical load failed");
    } finally {
      setHistoricalLoading(false);
    }
  };

  const subscribeToRealtimeUpdates = () => {
    const channel = supabase
      .channel(`vitta:plan:${profile?.plan_id}`)
      .on(
        "postgres_changes",
        { event: "INSERT", schema: "public", table: "Vitta_Calls" },
        (payload) => {
          const newCall = mapToStockCall(payload.new);
          const sourceId = newCall.source_recommendation_id || newCall.id;

          if (newCall.trade_type === "positional") {
            setPositionalCalls((prev) => {
              const existingGroupIndex = prev.findIndex(
                (group) =>
                  (group[0].source_recommendation_id || group[0].id) ===
                  sourceId,
              );

              if (existingGroupIndex >= 0) {
                const updated = [...prev];
                updated[existingGroupIndex] = [
                  ...updated[existingGroupIndex],
                  newCall,
                ];
                return updated;
              } else {
                return [[newCall], ...prev];
              }
            });
          } else {
            setTodaysCalls((prev) => {
              const existingGroupIndex = prev.findIndex(
                (group) =>
                  (group[0].source_recommendation_id || group[0].id) ===
                  sourceId,
              );

              if (existingGroupIndex >= 0) {
                const updated = [...prev];
                updated[existingGroupIndex] = [
                  ...updated[existingGroupIndex],
                  newCall,
                ];
                return updated;
              } else {
                return [[newCall], ...prev];
              }
            });
          }

          toast.success(`🔥 NEW CALL: ${newCall.instrumentName}`, {
            icon: "📈",
          });
        },
      )
      .subscribe();
    return () => {
      supabase.removeChannel(channel);
    };
  };

  // --- HELPERS ---
  const mapToStockCalls = (data: any[]): StockCall[] =>
    data.map(mapToStockCall);

  const mapToStockCall = (call: any): StockCall => ({
    id: call.call_id,
    instrumentName: call.name || "Unknown",
    instrumentType: call.instrument_type || "Instrument",
    action: call.action || "N/A",
    entryPrice: parseFloat(call.entry || "0"),
    targetPrice: parseFloat(call.target || "0"),
    exitPrice: call.exit || "",
    admincomment: call.admincomment || "",
    pastComments: call.admincomment,
    createdOn: call.created_on,
    status: callStatuses[call.call_id] || CallStatus.NONE,
    trade_type: call.trade_type || "N/A",
    source_recommendation_id: call.source_recommendation_id,
    stoploss: call.stoploss || "0",
  });



  const groupCallsBySourceRecommendation = (
    calls: StockCall[],
  ): StockCall[][] => {
    const grouped = new Map<string, StockCall[]>();

    calls.forEach((call) => {
      const sourceId = call.source_recommendation_id || call.id;
      if (!grouped.has(sourceId)) {
        grouped.set(sourceId, []);
      }
      grouped.get(sourceId)!.push(call);
    });

    return Array.from(grouped.values());
  };

  const handleStatusChange = (id: string, status: CallStatus) => {
    setCallStatuses((prev) => ({ ...prev, [id]: status }));

    const updateStatus = (groups: StockCall[][]): StockCall[][] =>
      groups.map((group) =>
        group.map((call) => (call.id === id ? { ...call, status } : call)),
      );

    setTodaysCalls(updateStatus);
    setPositionalCalls(updateStatus);
    setDateFilteredCalls(updateStatus);
  };

   const handleVote = (type: "profit" | "loss") => {
    setVoted(type);
    
    // Logic to send vote to Supabase/Backend would go here
  };

  const currentCalls =
    activeTab === "today"
      ? todaysCalls
      : activeTab === "positions"
        ? positionalCalls
        : dateFilteredCalls;

  return (
    <div className="min-h-screen bg-[#08090a] text-white relative overflow-hidden font-sans">
      {/* Dynamic Background Effects */}
      <div className="fixed inset-0 bg-[radial-gradient(circle_at_50%_0%,#1a1b23_0%,transparent_50%)] pointer-events-none" />
      <div className="fixed top-[-10%] left-[-10%] w-[40%] h-[40%] bg-accent-primary/5 rounded-full blur-[120px] pointer-events-none" />

      <Sidebar
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        user={profile}
        onSignOut={signOut}
      />

      {/* --- TOP NAVIGATION --- */}
      <nav className="sticky top-0 z-50 backdrop-blur-2xl bg-black/40 border-b border-white/5 px-4 py-3">
        <div className="flex items-center justify-between max-w-5xl mx-auto relative">
          <button
            onClick={() => setSidebarOpen(true)}
            className="p-2 hover:bg-white/5 rounded-xl transition-all"
          >
            <Menu size={22} className="text-accent-primary" />
          </button>

          {/* Centered Brand */}
          <div className="absolute left-1/2 -translate-x-1/2 flex items-center gap-2 group cursor-pointer">
            <div className="relative w-8 h-8">
              <div className="absolute inset-0 bg-accent-primary blur-md opacity-40 group-hover:opacity-100 transition-opacity"></div>
              <img
                src={logo}
                alt="V"
                className="relative w-full h-full object-cover rounded-lg border border-white/20"
              />
            </div>
            <span className="text-xl font-black tracking-tighter italic bg-gradient-to-r from-white to-orange-500 bg-clip-text text-transparent">
              ytta
            </span>
          </div>

          <button className="p-2 hover:bg-white/5 rounded-xl transition-all relative">
            <Bell size={22} className="text-gray-400" />
            <span className="absolute top-2 right-2 w-2 h-2 bg-accent-primary rounded-full ring-2 ring-black"></span>
          </button>
        </div>
      </nav>

      {/* --- LIVE MARKET TICKER --- */}
       <CarouselIndices indices={indices} autoplayMs={4000} />


      {/* --- SEXY TAB BAR --- */}
      <div className="px-6 mt-3">
        <div className="max-w-md mx-auto bg-[#111216] p-1 rounded-[1rem] border border-white/5 flex gap-1 shadow-2xl">
          {[
            { id: "today", icon: <TrendingUp size={18} />, label: "Intraday" },
            {
              id: "positions",
              icon: <History size={18} />,
              label: "Positions",
            },
            {
              id: "historical",
              icon: <Calendar size={18} />,
              label: "Historical",
            },
            {
              id: "sentiment",
              icon: <Activity size={18} />,
              label: "Sentiment",
            },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as any)}
              className={`flex-1 flex flex-col items-center py-3 rounded-[1.5rem] transition-all duration-300 ${
                activeTab === tab.id
                  ? "bg-gradient-to-br from-accent-primary to-accent-secondary text-white shadow-lg shadow-accent-primary/20 scale-[1.02]"
                  : "text-gray-500 hover:text-gray-300 hover:bg-white/5"
              }`}
            >
              {tab.icon}
              <span className="text-[9px] font-black mt-1 uppercase tracking-widest">
                {tab.label}
              </span>
            </button>
          ))}
        </div>
      </div>

      {/* --- CONDITIONAL FILTERS --- */}
      <div className="max-w-5xl mx-auto px-6 mt-3">
        {activeTab === "historical" && (
          <div className="bg-[#111216] rounded-3xl p-5 border border-white/5 shadow-xl animate-in fade-in slide-in-from-top-4">
            <h3 className="text-xs font-black text-gray-500 uppercase tracking-widest mb-4">
              Historical Query
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="bg-black/40 border border-white/10 rounded-xl px-4 py-3 text-sm focus:border-accent-primary outline-none"
              />
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="bg-black/40 border border-white/10 rounded-xl px-4 py-3 text-sm focus:border-accent-primary outline-none"
              />
            </div>
            <button
              onClick={loadHistoricalByDateRange}
              disabled={historicalLoading}
              className="w-full mt-4 py-3 bg-white text-black rounded-xl font-black text-xs tracking-widest hover:bg-gray-200 transition-all flex items-center justify-center gap-2"
            >
              <Search size={16} />{" "}
              {historicalLoading ? "SYNCING..." : "SEARCH ARCHIVES"}
            </button>
          </div>
        )}

        {activeTab === "sentiment" && (
          <div className="bg-[#111216] rounded-3xl p-5 border border-white/5 text-center">
            <h3 className="text-xs font-black text-gray-500 uppercase tracking-widest mb-4">
              Market Sentiment
            </h3>
            <div className="flex flex-wrap gap-2 justify-center mb-6">
              {timeframeOptions.map((t) => (
                <button
                  key={t}
                  onClick={() =>
                    setSelectedTimeframes((prev) =>
                      prev.includes(t)
                        ? prev.filter((x) => x !== t)
                        : [...prev, t],
                    )
                  }
                  className={`px-4 py-2 rounded-xl text-[10px] font-bold transition-all border ${selectedTimeframes.includes(t) ? "bg-accent-primary border-transparent shadow-lg" : "border-white/5 text-gray-500"}`}
                >
                  {t}
                </button>
              ))}
            </div>
            <div className="py-10 opacity-30">
              <Activity
                size={40}
                className="mx-auto mb-2 text-accent-primary animate-pulse"
              />
              <p className="text-sm font-bold tracking-widest italic">
                QUANT ALGO PROCESSING...
              </p>
            </div>
          </div>
        )}
      </div>

      {/* --- MAIN CALLS LIST --- */}
      <main className="px-6 py-2 max-w-5xl mx-auto pb-32">
        {loading ? (
          <div className="flex flex-col items-center justify-center py-20">
            <div className="w-10 h-10 border-2 border-accent-primary border-t-transparent rounded-full animate-spin mb-4" />
            <p className="text-[10px] font-black tracking-[0.3em] text-gray-500">
              INITIATING SECURE DATA STREAM...
            </p>
          </div>
        ) : currentCalls.length === 0 ? (
          <div className="text-center py-20 bg-white/[0.02] rounded-[3rem] border border-dashed border-white/5">
            <BarChart3 size={48} className="mx-auto text-gray-800 mb-4" />
            <h3 className="text-lg font-bold text-gray-500 italic">
              No Active Signals
            </h3>
            <p className="text-xs text-gray-600 mt-1 uppercase tracking-widest">
              Waiting for High-Probability Setup
            </p>
          </div>
        ) : (
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-2">
            {currentCalls.map((callGroup, idx) => (
              <StockCard
                key={callGroup[0].source_recommendation_id || callGroup[0].id}
                callGroup={callGroup}
              />
            ))}
          </div>
        )}
        
      </main>

      {/* --- FOOTER STATUS BAR --- */}
      <div className="fixed bottom-6 left-1/2 -translate-x-1/2 w-[90%] max-w-sm bg-black/60 backdrop-blur-2xl border border-white/10 rounded-full px-6 py-3 flex justify-between items-center z-40 shadow-2xl">
        <div className="flex items-center gap-2">
          <div className="w-2 h-2 bg-emerald-500 rounded-full animate-ping shadow-[0_0_10px_#10b981]"></div>
          <span className="text-[9px] font-black text-gray-400 uppercase tracking-[0.2em]">
            Live Connection
          </span>
        </div>
        <div className="text-[11px] font-mono text-accent-primary font-black">
          {new Date().toLocaleTimeString([], {
            hour: "2-digit",
            minute: "2-digit",
            second: "2-digit",
          })}
        </div>
      </div>
    </div>
  );
};

export default Home;
